import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { DateRangeScale, getDateRangeScale } from '../../common/utils/utils';
import type { DatePickerValue, PickerProps } from '../../types';

import { DayPicker } from './day-picker';
import { messages } from './messages';
import { MonthPicker } from './month-picker';
import { QuarterPicker } from './quarter-picker';

const buttonGroupStyles = xcss({
	padding: 'space.250',
	display: 'grid',
	gap: 'space.050',
	gridTemplateColumns: 'repeat(3, 1fr)',
	justifyContent: 'center',
});

const datePickerContentWrapperStyles = xcss({
	width: '322px',
});

const DEFAULT_GRANULARITIES = [
	DateRangeScale.DAY,
	DateRangeScale.MONTH,
	DateRangeScale.QUARTER,
] as const;

const DateRangePickerContent = ({
	withTimeScale = true,
	defaultRangeScale = DateRangeScale.MONTH,
	minDate,
	maxDate,
	granularities,
	...props
}: PickerProps): React.JSX.Element => {
	const { value: date } = props;
	const { formatMessage } = useIntl();

	const availableGranularities = granularities || DEFAULT_GRANULARITIES;

	const getInitialScale = () => {
		if (date) {
			const detectedScale = getDateRangeScale(date);
			return availableGranularities.includes(detectedScale)
				? detectedScale
				: availableGranularities[0];
		}
		return availableGranularities.includes(defaultRangeScale)
			? defaultRangeScale
			: availableGranularities[0];
	};

	const [scale, setScale] = useState(getInitialScale());

	const onChange = useCallback(
		(newDate?: DatePickerValue) => {
			if (newDate) {
				props.onChange(newDate, scale);
			}
		},
		[props, scale],
	);

	const onScaleChange = useCallback((newScale: DateRangeScale) => {
		setScale(newScale);
	}, []);

	const dateValue = date ? date.end : undefined;

	const shouldShowGranularity = (granularity: DateRangeScale) =>
		availableGranularities.includes(granularity);

	const shouldShowSwitcher = withTimeScale && availableGranularities.length > 1;

	return (
		<Box xcss={datePickerContentWrapperStyles}>
			<Box testId="date-picker-button-wrapper" role="tablist">
				{shouldShowSwitcher && (
					<Box xcss={buttonGroupStyles}>
						{shouldShowGranularity(DateRangeScale.DAY) && (
							<Button
								isSelected={scale === DateRangeScale.DAY}
								onClick={() => onScaleChange(DateRangeScale.DAY)}
								testId="day-picker-button"
								aria-label={DateRangeScale.DAY}
								aria-selected={scale === DateRangeScale.DAY}
								role="tab"
							>
								{formatMessage(messages.dayPickerButtonMessage)}
							</Button>
						)}
						{shouldShowGranularity(DateRangeScale.MONTH) && (
							<Button
								isSelected={scale === DateRangeScale.MONTH}
								onClick={() => onScaleChange(DateRangeScale.MONTH)}
								testId="month-picker-button"
								aria-label={DateRangeScale.MONTH}
								aria-selected={scale === DateRangeScale.MONTH}
								role="tab"
							>
								{formatMessage(messages.monthPickerButtonMessage)}
							</Button>
						)}
						{shouldShowGranularity(DateRangeScale.QUARTER) && (
							<Button
								isSelected={scale === DateRangeScale.QUARTER}
								onClick={() => onScaleChange(DateRangeScale.QUARTER)}
								testId="quarter-picker-button"
								aria-label={DateRangeScale.QUARTER}
								aria-selected={scale === DateRangeScale.QUARTER}
								role="tab"
							>
								{formatMessage(messages.quarterPickerButtonMessage)}
							</Button>
						)}
					</Box>
				)}
			</Box>
			<Box testId="date-picker-content-wrapper">
				{scale === DateRangeScale.DAY && (
					<DayPicker date={dateValue} onSelect={onChange} minDate={minDate} maxDate={maxDate} />
				)}
				{scale === DateRangeScale.MONTH && (
					<MonthPicker
						date={dateValue}
						onSelect={onChange}
						withTimeScale={withTimeScale}
						minDate={minDate}
						maxDate={maxDate}
					/>
				)}
				{scale === DateRangeScale.QUARTER && (
					<QuarterPicker date={dateValue} onSelect={onChange} minDate={minDate} maxDate={maxDate} />
				)}
			</Box>
		</Box>
	);
};

export default DateRangePickerContent;
